import axios from 'axios';

const getAuthToken = (): string | null => {
  if (typeof document === 'undefined') return null;
  const cookies = document.cookie.split(';');
  for (const cookie of cookies) {
    const [name, value] = cookie.trim().split('=');
    if (name === 'session_token') {
      return value;
    }
  }
  return null;
};

const getUserId = (): string | null => {
  if (typeof document === 'undefined') return null;
  const cookies = document.cookie.split(';');
  for (const cookie of cookies) {
    const [name, value] = cookie.trim().split('=');
    if (name === 'user_id') return value;
  }
  return null;
};

const BFF_API_URL = import.meta.env.VITE_BFF_API_URL || 'http://localhost:8086';
const AUTH_APP_URL = import.meta.env.VITE_AUTH_APP_URL || 'http://localhost:3000';
const ttClient = axios.create({
  baseURL: BFF_API_URL,
  withCredentials: true,
  headers: { 'Content-Type': 'application/json' },
});

ttClient.interceptors.request.use(config => {
  const token = getAuthToken();
  if (token && config.headers) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  const userId = getUserId();
  if (userId && config.headers) {
    config.headers['X-User-ID'] = userId;
  }
  return config;
});

ttClient.interceptors.response.use(
  response => response,
  error => {
    if (error.response?.status === 401 && typeof window !== 'undefined') {
      window.location.href = AUTH_APP_URL;
    }
    return Promise.reject(error);
  },
);

// ---- Types ----

export interface PricingBreakdownItem {
  label: string;
  rate: number;
  quantity: number;
  expression: string;
  total: number;
  category: 'accommodation' | 'transport' | 'supplement';
}

export interface PricingBreakdown {
  items: PricingBreakdownItem[];
  total: number;
  currency: string;
}

export interface BookingCreateResponse {
  booking: TourBooking;
  pricing_breakdown: PricingBreakdown;
}

export interface TourSupplier {
  id: string;
  name: string;
  category: 'hotel' | 'vehicle' | 'excursion' | 'activity';
  contact_person: string;
  email: string;
  phone: string;
  location: string;
  latitude: number;
  longitude: number;
  rating: number;
  contract_status: 'active' | 'expiring' | 'inactive';
  contract_expiry: string;
  notes: string;
  created_at: string;
  updated_at: string;
}

export interface TourPackage {
  id: string;
  name: string;
  duration: number;
  type: 'safari' | 'beach' | 'cultural' | 'mountain' | 'city';
  destination: string;
  base_price: number;
  inclusions: string[];
  exclusions: string[];
  available: boolean;
  min_group_size: number;
  max_group_size: number;
  created_at: string;
  updated_at: string;
}

export interface BookingSupplierItem {
  id: string;
  booking_id: string;
  supplier_id: string;
  supplier: TourSupplier | null;
  role: string;
  created_at: string;
}

export interface BookingPackageItem {
  id: string;
  booking_id: string;
  package_id: string;
  package: TourPackage | null;
  quantity: number;
  unit_price: number;
  created_at: string;
}

export interface BookingGuest {
  id: string;
  booking_id: string;
  name: string;
  age: number | null;
  is_child: boolean;
  id_number: string;
  created_at: string;
}

export interface PaymentPlan {
  id: string;
  booking_id: string;
  name: string;
  total_amount: number;
  status: 'active' | 'completed' | 'cancelled';
  installments: PaymentInstallment[];
  created_at: string;
  updated_at: string;
}

export interface PaymentInstallment {
  id: string;
  plan_id: string;
  installment_number: number;
  label: string;
  amount: number;
  due_date: string | null;
  paid_date: string | null;
  status: 'pending' | 'paid' | 'overdue' | 'skipped';
  payment_id: string | null;
  created_at: string;
  updated_at: string;
}

export interface InvoiceLineItem {
  id: string;
  invoice_id: string;
  description: string;
  quantity: number;
  unit_price: number;
  amount: number;
  category: string;
  created_at: string;
}

export interface TourBooking {
  id: string;
  reference: string;
  client_id: string;
  client: TravelClient | null;
  supplier_id: string;
  supplier: TourSupplier | null;
  package_id: string;
  package: TourPackage | null;
  booking_suppliers: BookingSupplierItem[];
  booking_packages: BookingPackageItem[];
  guests: BookingGuest[];
  payment_plans: PaymentPlan[];
  client_name: string;
  client_email: string;
  client_phone: string;
  client_alt_phone: string;
  client_birthday: string;
  client_birth_month: string;
  client_anniversary: string;
  client_nationality: string;
  guest_count: number;
  adult_count: number;
  child_count: number;
  child_rate: number;
  child_rate_note: string;
  rate_class: string;
  rate_per_adult: number;
  amount_payable: number;
  guest_names: string;
  destination: string;
  destination_type: string;
  check_in: string;
  check_out: string;
  total_amount: number;
  commission: number;
  deposit_amount: number;
  amount_paid: number;
  status: 'draft' | 'confirmed' | 'pending' | 'cancelled' | 'completed';
  payment_status: 'unpaid' | 'partial' | 'paid';
  transport_mode: string;
  transport_class: string;
  vehicle_type: string;
  vehicle_rate: number;
  pickup_location: string;
  dropoff_location: string;
  transport_notes: string;
  transport_capacity: string;
  transport_legs: {
    type: string;
    route: string;
    pickup: string;
    dropoff: string;
    capacity: string;
    price: number;
  }[];
  room_type: string;
  board_type: string;
  room_configuration: { double: number; twin: number; triple: number; single: number };
  itinerary_legs: {
    sequence: number;
    destination: string;
    accommodation: string;
    nights: number;
    supplier_id?: string;
    meal_plan?: string;
    room_type?: string;
    transport_mode?: string;
    rate_adult?: number;
    rate_child?: number;
    rate_single?: number;
    rate_extra_adult?: number;
    rate_mode?: string;
    comm_type?: string;
    comm_val?: number;
    notes?: string;
  }[];
  trip_description: string;
  seats: number;
  departure_time: string;
  return_time: string;
  included_items: string[];
  excluded_items: string[];
  carry_documents: string[];
  other_inclusions: string;
  other_exclusions: string;
  other_documents: string;
  payment_terms: string;
  cancellation_policy: string;
  voucher_number: string;
  invoice_number_ref: string;
  served_by_tc: string;
  director_seen: boolean;
  client_signed: boolean;
  operational_warnings: string[];
  special_requests: string;
  custom_items: { name: string; price: number; quantity: number }[];
  payments: TourBookingPayment[];
  notes: string;
  created_at: string;
  updated_at: string;
}

export interface TravelClient {
  id: string;
  name: string;
  email: string;
  phone: string;
  alt_phone: string;
  birthday: string;
  birth_month: string;
  anniversary: string;
  nationality: string;
  id_type: 'passport' | 'national_id';
  id_number: string;
  id_expiry: string;
  visa_required: boolean;
  address: string;
  created_at: string;
  updated_at: string;
}

export interface TourBookingPayment {
  id: string;
  booking_id: string;
  amount: number;
  payment_method: 'mpesa' | 'cash' | 'card';
  status: 'pending' | 'completed' | 'failed' | 'refunded';
  reference: string;
  transaction_id: string;
  notes: string;
  created_at: string;
}

export interface TourInvoice {
  id: string;
  invoice_number: string;
  booking_id: string;
  booking: TourBooking | null;
  client_name: string;
  supplier_id: string;
  supplier: TourSupplier | null;
  package_name: string;
  amount: number;
  commission: number;
  status: 'issued' | 'paid' | 'overdue' | 'cancelled';
  due_date: string;
  paid_date: string | null;
  line_items: InvoiceLineItem[];
  created_at: string;
  updated_at: string;
}

interface PaginatedResponse<T> {
  data: T[];
  total: number;
  page: number;
  page_size: number;
  total_pages: number;
}

interface CreateSupplierInput {
  name: string;
  category: string;
  contact_person?: string;
  email?: string;
  phone?: string;
  location?: string;
  latitude?: number;
  longitude?: number;
  rating?: number;
  contract_status?: string;
  contract_expiry?: string;
  notes?: string;
}

interface CreatePackageInput {
  name: string;
  duration: number;
  type: string;
  base_price: number;
  inclusions?: string[];
  exclusions?: string[];
  available?: boolean;
  min_group_size?: number;
  max_group_size?: number;
}

interface CreateBookingInput {
  reference: string;
  client_id?: string;
  supplier_id?: string;
  package_id?: string;
  booking_suppliers?: { supplier_id: string; role?: string }[];
  booking_packages?: { package_id: string; quantity?: number; unit_price?: number }[];
  custom_items?: { name: string; price: number; quantity: number }[];
  guests?: { name: string; age?: number | null; is_child?: boolean; id_number?: string }[];
  client_name: string;
  client_email?: string;
  client_phone?: string;
  client_alt_phone?: string;
  client_birthday?: string;
  client_birth_month?: string;
  client_anniversary?: string;
  client_nationality?: string;
  guest_count?: number;
  adult_count?: number;
  child_count?: number;
  child_rate?: number;
  child_rate_note?: string;
  rate_class?: string;
  rate_per_adult?: number;
  amount_payable?: number;
  guest_names?: string;
  destination?: string;
  destination_type?: string;
  check_in?: string;
  check_out?: string;
  status?: string;
  payment_status?: string;
  transport_mode?: string;
  transport_class?: string;
  vehicle_type?: string;
  vehicle_rate?: number;
  pickup_location?: string;
  dropoff_location?: string;
  room_type?: string;
  board_type?: string;
  room_configuration?: { double: number; twin: number; triple: number; single: number };
  itinerary_legs?: {
    sequence: number;
    destination: string;
    accommodation: string;
    nights: number;
    supplier_id?: string;
    meal_plan?: string;
    room_type?: string;
    transport_mode?: string;
    rate_adult?: number;
    rate_child?: number;
    rate_single?: number;
    rate_extra_adult?: number;
    rate_mode?: string;
    comm_type?: string;
    comm_val?: number;
    notes?: string;
  }[];
  trip_description?: string;
  seats?: number;
  departure_time?: string;
  return_time?: string;
  included_items?: string[];
  excluded_items?: string[];
  carry_documents?: string[];
  other_inclusions?: string;
  other_exclusions?: string;
  other_documents?: string;
  payment_terms?: string;
  cancellation_policy?: string;
  voucher_number?: string;
  invoice_number_ref?: string;
  served_by_tc?: string;
  director_seen?: boolean;
  client_signed?: boolean;
  special_requests?: string;
  notes?: string;
}

interface CreateClientInput {
  name: string;
  email?: string;
  phone?: string;
  alt_phone?: string;
  birthday?: string;
  birth_month?: string;
  anniversary?: string;
  nationality?: string;
  id_type?: string;
  id_number?: string;
  id_expiry?: string;
  visa_required?: boolean;
  address?: string;
}

interface CreateInvoiceInput {
  invoice_number: string;
  booking_id?: string;
  client_name?: string;
  supplier_id?: string;
  package_name?: string;
  amount: number;
  commission?: number;
  status?: string;
  due_date?: string;
}

export const toursTravelsService = {
  // Suppliers
  listSuppliers: async (params?: { page?: number; page_size?: number }) => {
    const res = await ttClient.get<PaginatedResponse<TourSupplier>>(
      '/api/v1/tours-travels/suppliers',
      { params },
    );
    return res.data;
  },
  getSupplier: (id: string) =>
    ttClient.get<TourSupplier>(`/api/v1/tours-travels/suppliers/${id}`).then(r => r.data),
  createSupplier: (data: CreateSupplierInput) =>
    ttClient.post<TourSupplier>('/api/v1/tours-travels/suppliers', data).then(r => r.data),
  updateSupplier: (id: string, data: Partial<CreateSupplierInput>) =>
    ttClient.put<TourSupplier>(`/api/v1/tours-travels/suppliers/${id}`, data).then(r => r.data),
  deleteSupplier: (id: string) => ttClient.delete(`/api/v1/tours-travels/suppliers/${id}`),

  // Packages
  listPackages: async (params?: { page?: number; page_size?: number }) => {
    const res = await ttClient.get<PaginatedResponse<TourPackage>>(
      '/api/v1/tours-travels/tour-packages',
      { params },
    );
    return res.data;
  },
  getPackage: (id: string) =>
    ttClient.get<TourPackage>(`/api/v1/tours-travels/tour-packages/${id}`).then(r => r.data),
  createPackage: (data: CreatePackageInput) =>
    ttClient.post<TourPackage>('/api/v1/tours-travels/tour-packages', data).then(r => r.data),
  updatePackage: (id: string, data: Partial<CreatePackageInput>) =>
    ttClient.put<TourPackage>(`/api/v1/tours-travels/tour-packages/${id}`, data).then(r => r.data),
  deletePackage: (id: string) => ttClient.delete(`/api/v1/tours-travels/tour-packages/${id}`),

  // Bookings
  listBookings: async (params?: { page?: number; page_size?: number }) => {
    const res = await ttClient.get<PaginatedResponse<TourBooking>>(
      '/api/v1/tours-travels/tour-bookings',
      { params },
    );
    return res.data;
  },
  getBooking: (id: string) =>
    ttClient.get<TourBooking>(`/api/v1/tours-travels/tour-bookings/${id}`).then(r => r.data),
  createBooking: (data: CreateBookingInput) =>
    ttClient
      .post<BookingCreateResponse>('/api/v1/tours-travels/tour-bookings', data)
      .then(r => r.data),
  updateBooking: (id: string, data: Partial<CreateBookingInput>) =>
    ttClient
      .put<BookingCreateResponse>(`/api/v1/tours-travels/tour-bookings/${id}`, data)
      .then(r => r.data),
  pricingPreview: (data: CreateBookingInput) =>
    ttClient
      .post<PricingBreakdown>('/api/v1/tours-travels/tour-bookings/pricing-preview', data)
      .then(r => r.data),
  deleteBooking: (id: string) => ttClient.delete(`/api/v1/tours-travels/tour-bookings/${id}`),
  cancelBooking: (id: string) => ttClient.post(`/api/v1/tours-travels/tour-bookings/${id}/cancel`),
  recordPayment: (
    bookingId: string,
    data: {
      amount: number;
      payment_method: string;
      reference?: string;
      transaction_id?: string;
      notes?: string;
    },
  ) => ttClient.post(`/api/v1/tours-travels/tour-bookings/${bookingId}/payments`, data),
  getPayments: (bookingId: string) =>
    ttClient.get<TourBookingPayment[]>(`/api/v1/tours-travels/tour-bookings/${bookingId}/payments`),

  // Clients
  listClients: async (params?: { page?: number; page_size?: number }) => {
    const res = await ttClient.get<PaginatedResponse<TravelClient>>(
      '/api/v1/tours-travels/travel-clients',
      { params },
    );
    return res.data;
  },
  getClient: (id: string) =>
    ttClient.get<TravelClient>(`/api/v1/tours-travels/travel-clients/${id}`).then(r => r.data),
  createClient: (data: CreateClientInput) =>
    ttClient.post<TravelClient>('/api/v1/tours-travels/travel-clients', data).then(r => r.data),
  updateClient: (id: string, data: Partial<CreateClientInput>) =>
    ttClient
      .put<TravelClient>(`/api/v1/tours-travels/travel-clients/${id}`, data)
      .then(r => r.data),
  deleteClient: (id: string) => ttClient.delete(`/api/v1/tours-travels/travel-clients/${id}`),

  // Invoices
  listInvoices: async (params?: { page?: number; page_size?: number }) => {
    const res = await ttClient.get<PaginatedResponse<TourInvoice>>(
      '/api/v1/tours-travels/invoices',
      { params },
    );
    return res.data;
  },
  getInvoice: (id: string) =>
    ttClient.get<TourInvoice>(`/api/v1/tours-travels/invoices/${id}`).then(r => r.data),
  createInvoice: (data: CreateInvoiceInput) =>
    ttClient.post<TourInvoice>('/api/v1/tours-travels/invoices', data).then(r => r.data),
  updateInvoice: (id: string, data: Partial<CreateInvoiceInput>) =>
    ttClient.put<TourInvoice>(`/api/v1/tours-travels/invoices/${id}`, data).then(r => r.data),
  deleteInvoice: (id: string) => ttClient.delete(`/api/v1/tours-travels/invoices/${id}`),
  markInvoicePaid: (id: string) => ttClient.post(`/api/v1/tours-travels/invoices/${id}/mark-paid`),

  // Payment Plans
  createPaymentPlan: (
    bookingId: string,
    data: {
      name: string;
      total_amount: number;
      installments: {
        installment_number: number;
        label: string;
        amount: number;
        due_date?: string;
      }[];
    },
  ) =>
    ttClient.post<PaymentPlan>(
      `/api/v1/tours-travels/tour-bookings/${bookingId}/payment-plans`,
      data,
    ),
  updatePaymentPlan: (
    planId: string,
    data: {
      name: string;
      total_amount: number;
      installments: {
        installment_number: number;
        label: string;
        amount: number;
        due_date?: string;
      }[];
    },
  ) => ttClient.put<PaymentPlan>(`/api/v1/tours-travels/payment-plans/${planId}`, data),
  listPaymentPlans: (bookingId: string) =>
    ttClient.get<PaymentPlan[]>(`/api/v1/tours-travels/tour-bookings/${bookingId}/payment-plans`),
  getPaymentPlan: (id: string) =>
    ttClient.get<PaymentPlan>(`/api/v1/tours-travels/payment-plans/${id}`).then(r => r.data),
  deletePaymentPlan: (id: string) => ttClient.delete(`/api/v1/tours-travels/payment-plans/${id}`),
  payInstallment: (installmentId: string, data: { payment_id: string }) =>
    ttClient.post<PaymentInstallment>(
      `/api/v1/tours-travels/payment-plans/installments/${installmentId}/pay`,
      data,
    ),
  listInstallments: (planId: string) =>
    ttClient.get<PaymentInstallment[]>(
      `/api/v1/tours-travels/payment-plans/${planId}/installments`,
    ),

  // Booking confirm (returns invoice info)
  confirmBooking: (id: string) =>
    ttClient.post<{ message: string; invoice_id?: string; invoice_number?: string }>(
      `/api/v1/tours-travels/tour-bookings/${id}/confirm`,
    ),

  // ---- Excursions ----
  createExcursion: (bookingId: string, data: any) =>
    ttClient.post(`/api/v1/tours-travels/tour-bookings/${bookingId}/excursions`, data),
  listExcursions: (bookingId: string) =>
    ttClient.get(`/api/v1/tours-travels/tour-bookings/${bookingId}/excursions`),
  updateExcursion: (id: string, data: any) =>
    ttClient.put(`/api/v1/tours-travels/excursions/${id}`, data),
  deleteExcursion: (id: string) => ttClient.delete(`/api/v1/tours-travels/excursions/${id}`),

  // ---- Sign-offs ----
  signBooking: (
    bookingId: string,
    data: { signoff_type: string; signer_name: string; signer_email?: string; notes?: string },
  ) => ttClient.post(`/api/v1/tours-travels/tour-bookings/${bookingId}/signoffs`, data),
  listSignoffs: (bookingId: string) =>
    ttClient.get(`/api/v1/tours-travels/tour-bookings/${bookingId}/signoffs`),

  // ---- Financials ----
  getFinancials: (bookingId: string) =>
    ttClient.get(`/api/v1/tours-travels/tour-bookings/${bookingId}/financials`),
  updateFinancials: (bookingId: string, data: any) =>
    ttClient.put(`/api/v1/tours-travels/tour-bookings/${bookingId}/financials`, data),
  updateExpenses: (bookingId: string, data: any) =>
    ttClient.put(`/api/v1/tours-travels/tour-bookings/${bookingId}/expenses`, data),
  listExpenses: (bookingId: string) =>
    ttClient.get(`/api/v1/tours-travels/tour-bookings/${bookingId}/expenses`),
  approveExpense: (bookingId: string, expenseId: string) =>
    ttClient.post(`/api/v1/tours-travels/tour-bookings/${bookingId}/expenses/${expenseId}/approve`),
  updateReceipts: (bookingId: string, data: any) =>
    ttClient.put(`/api/v1/tours-travels/tour-bookings/${bookingId}/receipts`, data),
  listReceipts: (bookingId: string) =>
    ttClient.get(`/api/v1/tours-travels/tour-bookings/${bookingId}/receipts`),

  // ---- Rate Cards ----
  listRateCards: (params?: any) =>
    ttClient.get('/api/v1/tours-travels/supplier-rate-cards', { params }),
  createRateCard: (data: any) => ttClient.post('/api/v1/tours-travels/supplier-rate-cards', data),
  bulkReplaceRateCards: (data: { supplier_id: string; name: string; rows: any[] }) =>
    ttClient.post('/api/v1/tours-travels/supplier-rate-cards/bulk-replace', data),
  updateRateCard: (id: string, data: any) =>
    ttClient.put(`/api/v1/tours-travels/supplier-rate-cards/${id}`, data),
  deleteRateCard: (id: string) =>
    ttClient.delete(`/api/v1/tours-travels/supplier-rate-cards/${id}`),
  getRateCardsBySupplier: (supplierId: string, date?: string) =>
    ttClient.get(`/api/v1/tours-travels/supplier-rate-cards/supplier/${supplierId}`, {
      params: { date },
    }),

  // ---- Group Bookings ----
  listGroupBookings: (params?: any) =>
    ttClient.get('/api/v1/tours-travels/group-bookings', { params }),
  createGroupBooking: (data: any) => ttClient.post('/api/v1/tours-travels/group-bookings', data),
  getGroupBooking: (id: string) => ttClient.get(`/api/v1/tours-travels/group-bookings/${id}`),
  updateGroupBooking: (id: string, data: any) =>
    ttClient.put(`/api/v1/tours-travels/group-bookings/${id}`, data),
  deleteGroupBooking: (id: string) => ttClient.delete(`/api/v1/tours-travels/group-bookings/${id}`),

  // ---- Reporting ----
  getDashboardStats: () => ttClient.get('/api/v1/tours-travels/reporting/dashboard'),
  getFinancialSummary: (params?: any) =>
    ttClient.get('/api/v1/tours-travels/reporting/financial-summary', { params }),

  // ---- Accounts (cross-booking) ----
  listAllBookingExpenses: () => ttClient.get('/api/v1/tours-travels/accounts/booking-expenses'),
  listAllBookingExpensesSummary: () =>
    ttClient.get('/api/v1/tours-travels/accounts/booking-expenses/summary'),

  // ---- Operational Expenses ----
  listOperationalExpenses: (params?: any) =>
    ttClient.get('/api/v1/tours-travels/operational-expenses', { params }),
  listAllOperationalExpenses: () => ttClient.get('/api/v1/tours-travels/operational-expenses/all'),
  getOperationalExpenseTotal: () =>
    ttClient.get('/api/v1/tours-travels/operational-expenses/total'),
  createOperationalExpense: (data: any) =>
    ttClient.post('/api/v1/tours-travels/operational-expenses', data),
  updateOperationalExpense: (id: string, data: any) =>
    ttClient.put(`/api/v1/tours-travels/operational-expenses/${id}`, data),
  deleteOperationalExpense: (id: string) =>
    ttClient.delete(`/api/v1/tours-travels/operational-expenses/${id}`),
};

// ---- Public API (no auth) ----

const publicClient = axios.create({
  baseURL: BFF_API_URL,
  withCredentials: false,
  headers: { 'Content-Type': 'application/json' },
});

export interface PublicBookingRequest {
  name: string;
  email: string;
  phone?: string;
  package_id?: string;
  preferred_date?: string;
  guest_count?: number;
  message?: string;
}

export const publicToursService = {
  createBookingRequest: (data: PublicBookingRequest) =>
    publicClient.post<{ message: string; booking_id: string; reference: string }>(
      '/api/v1/tours-travels/public/booking-request',
      data,
    ),
};
